import { useEffect, useState } from 'react'
import { tournamentApi } from '../services/api'

/**
 * Shared API-key management UI.
 *
 * mode='admin'  — InteGolf super-admin managing a reseller's keys
 *                 (issue pair, add key, rotate, revoke, edit domains).
 *                 Requires `accountId`.
 * mode='portal' — a reseller managing their own keys (view, rotate a
 *                 secret, edit allowed domains). No issue/revoke.
 *
 * Endpoints:
 *   admin  -> /admin/resellers/accounts/:id/api-keys[...]
 *   portal -> /portal/reseller/api-keys[...]
 *
 * Secret tokens come back only on create/rotate; we surface them once in a
 * copy-me banner and never store them.
 */
export default function ApiKeysManager({ mode = 'portal', accountId, docsHref = '/developers' }) {
  const isAdmin = mode === 'admin'
  const base = isAdmin ? `/admin/resellers/accounts/${accountId}/api-keys` : `/portal/reseller/api-keys`

  const [keys, setKeys] = useState(null)
  const [err, setErr] = useState(null)
  const [env, setEnv] = useState(null) // 'dev' | 'live' — which environment served these keys
  const [reveal, setReveal] = useState(null) // { publishable?, secret?, single? } one-time tokens
  const [busy, setBusy] = useState(false)

  function load() {
    setErr(null)
    tournamentApi.get(base)
      .then(r => { setKeys(r.data.keys); setEnv(r.data.environment || null) })
      .catch(e => setErr(e?.response?.data?.message || e.message))
  }
  useEffect(load, [base])

  async function issuePair() {
    const domains = prompt('Allowed website domains for the publishable key (comma-separated).\nwww and the apex are SEPARATE — add both if you use both.\nExample: capebretonfirst.com, www.capebretonfirst.com')
    if (domains === null) return
    const list = domains.split(',').map(s => s.trim()).filter(Boolean)
    const bad = validateDomains(list)
    if (bad.length) { setErr('Fix these domains before continuing — ' + bad.join('; ')); return }
    setBusy(true); setErr(null)
    try {
      const r = await tournamentApi.post(base, {
        mode: 'pair',
        label: 'Default',
        allowedDomains: list,
      })
      setReveal({ publishable: r.data.publishable?.token, secret: r.data.secret?.token })
      load()
    } catch (e) { setErr(e?.response?.data?.message || e.message) }
    finally { setBusy(false) }
  }

  // Reseller self-serve SANDBOX keys (pk_test_/sk_test_). A test key books into a
  // fully-faked lane — no real supplier call, no charge — so resellers can build
  // and test their integration safely. Live keys stay admin-issued.
  async function createSandbox() {
    const domains = prompt('Allowed website domains for the sandbox publishable key (comma-separated).\nLeave blank if you will only use the secret key from a server.\nExample: localhost, staging.yoursite.com')
    if (domains === null) return
    const list = domains.split(',').map(s => s.trim()).filter(Boolean)
    if (list.length) {
      const bad = validateDomains(list)
      if (bad.length) { setErr('Fix these domains before continuing — ' + bad.join('; ')); return }
    }
    setBusy(true); setErr(null)
    try {
      // Portal has a dedicated self-serve endpoint that forces environment=test;
      // admin reuses the normal create with environment:'test'.
      const url = isAdmin ? base : `${base}/sandbox`
      const body = isAdmin
        ? { mode: 'pair', environment: 'test', label: 'Sandbox', allowedDomains: list }
        : { mode: 'pair', label: 'Sandbox', allowedDomains: list }
      const r = await tournamentApi.post(url, body)
      setReveal({ publishable: r.data.publishable?.token, secret: r.data.secret?.token })
      load()
    } catch (e) { setErr(e?.response?.data?.message || e.message) }
    finally { setBusy(false) }
  }

  async function rotate(key) {
    if (!confirm(`Rotate the ${key.keyType} key ${key.keyPrefix}?\n\nThe old key stops working immediately and a new one is generated. You'll see the new value once.`)) return
    setBusy(true); setErr(null)
    try {
      const r = await tournamentApi.post(`${base}/${key.id}/rotate`)
      setReveal({ single: r.data.key?.token, keyType: key.keyType })
      load()
    } catch (e) { setErr(e?.response?.data?.message || e.message) }
    finally { setBusy(false) }
  }

  async function revoke(key) {
    if (!confirm(`Revoke ${key.keyType} key ${key.keyPrefix}? This cannot be undone — any integration using it will stop working.`)) return
    setBusy(true); setErr(null)
    try {
      await tournamentApi.delete(`${base}/${key.id}`)
      load()
    } catch (e) { setErr(e?.response?.data?.message || e.message) }
    finally { setBusy(false) }
  }

  async function editDomains(key) {
    const next = prompt('Allowed website domains (comma-separated). www and the apex are SEPARATE — add both if you use both.\nExample: capebretonfirst.com, www.capebretonfirst.com', (key.allowedDomains || []).join(', '))
    if (next === null) return
    const list = next.split(',').map(s => s.trim()).filter(Boolean)
    const bad = validateDomains(list)
    if (bad.length) { setErr('Fix these domains — ' + bad.join('; ')); return }
    const domainsUrl = isAdmin ? `${base}/${key.id}` : `${base}/${key.id}/domains`
    setBusy(true); setErr(null)
    try {
      await tournamentApi.patch(domainsUrl, { allowedDomains: list })
      load()
    } catch (e) { setErr(e?.response?.data?.message || e.message) }
    finally { setBusy(false) }
  }

  // SEC #176: optional per-key IP allowlist. Empty = any IP (so browser-embedded
  // keys keep working); when set, requests must come from a listed IP/CIDR.
  async function editIps(key) {
    const next = prompt(
      'Allowed IP addresses / CIDR ranges for this key (comma-separated).\nLeave blank to allow any IP (needed for browser/front-end use).\nExample: 203.0.113.10, 198.51.100.0/24',
      (key.allowedIps || []).join(', ')
    )
    if (next === null) return
    const url = isAdmin ? `${base}/${key.id}` : `${base}/${key.id}/domains`
    setBusy(true); setErr(null)
    try {
      await tournamentApi.patch(url, { allowedIps: next.split(',').map(s => s.trim()).filter(Boolean) })
      load()
    } catch (e) { setErr(e?.response?.data?.message || e.message) }
    finally { setBusy(false) }
  }

  // One-click add of a recently-seen egress IP to the key's allowlist. Pairs
  // with the /connection-check endpoint, which lists the IPs we've observed.
  async function addIp(key, ip) {
    const current = key.allowedIps || []
    if (current.includes(ip)) return
    const url = isAdmin ? `${base}/${key.id}` : `${base}/${key.id}/domains`
    setBusy(true); setErr(null)
    try {
      await tournamentApi.patch(url, { allowedIps: [...current, ip] })
      load()
    } catch (e) { setErr(e?.response?.data?.message || e.message) }
    finally { setBusy(false) }
  }

  // SEC #176: booking-volume alert threshold (bookings/hour) for this key.
  async function editAlert(key) {
    const cur = key.bookingAlertPerHour == null ? '' : String(key.bookingAlertPerHour)
    const next = prompt(
      'Email an alert if this key creates more than N bookings per hour.\nBlank = default (publishable keys 50/hr), 0 = off.',
      cur
    )
    if (next === null) return
    const t = next.trim()
    const val = t === '' ? null : Math.max(0, parseInt(t, 10) || 0)
    const url = isAdmin ? `${base}/${key.id}` : `${base}/${key.id}/domains`
    setBusy(true); setErr(null)
    try {
      await tournamentApi.patch(url, { bookingAlertPerHour: val })
      load()
    } catch (e) { setErr(e?.response?.data?.message || e.message) }
    finally { setBusy(false) }
  }

  if (err && !keys) return <div style={errorBox}>{err}</div>
  if (!keys) return <div style={{ color: '#64748b', fontSize: 14 }}>Loading keys…</div>

  const active = keys.filter(k => k.isActive)
  const activeLive = active.filter(k => (k.environment || 'live') !== 'test')
  const activeTest = active.filter(k => (k.environment || 'live') === 'test')
  const revoked = keys.filter(k => !k.isActive)
  // The self-service connection-check endpoint (same origin as the partner API).
  const checkUrl = `${String(tournamentApi.defaults.baseURL || '').replace(/\/$/, '')}/partner/v1/connection-check`

  return (
    <div>
      {env && <EnvBanner env={env} />}

      {reveal && <RevealBanner reveal={reveal} onClose={() => setReveal(null)} />}

      {err && <div style={{ ...errorBox, marginBottom: 12 }}>{err}</div>}

      {/* ── Live keys ──────────────────────────────────────────────────── */}
      {activeLive.length === 0 ? (
        <div style={{ background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 8, padding: 20, textAlign: 'center' }}>
          <div style={{ color: '#475569', fontSize: 14, marginBottom: 12 }}>
            No live API keys yet.{isAdmin ? ' Issue a key pair to give this reseller API access alongside their embed code.' : ' Ask InteGolf to issue your live API access.'}
          </div>
          {isAdmin && <button onClick={issuePair} disabled={busy} style={primaryBtn}>Issue API access (publishable + secret)</button>}
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {activeLive.map(k => (
              <KeyRow key={k.id} k={k} isAdmin={isAdmin} busy={busy} env={env} checkUrl={checkUrl}
                onRotate={() => rotate(k)} onRevoke={() => revoke(k)} onEditDomains={() => editDomains(k)}
                onEditIps={() => editIps(k)} onEditAlert={() => editAlert(k)} onAddIp={(ip) => addIp(k, ip)} />
            ))}
          </div>
          {isAdmin && (
            <button onClick={issuePair} disabled={busy} style={{ ...secondaryBtn, marginTop: 12 }}>
              + Issue another pair
            </button>
          )}
        </>
      )}

      {/* ── Sandbox (test mode) ───────────────────────────────────────────
          Self-serve test keys. A booking made with a sandbox key is fully
          faked — no real hotel/golf reservation and no card is ever charged —
          so partners can build and test end-to-end safely. */}
      <div style={{ marginTop: 22, paddingTop: 18, borderTop: '1px solid #e2e8f0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <h4 style={{ margin: 0, fontSize: 15, color: '#0f172a' }}>Sandbox</h4>
          <span style={pill('#fef3c7', '#92400e')}>test mode</span>
        </div>
        <p style={{ fontSize: 12.5, color: '#64748b', margin: '0 0 12px', lineHeight: 1.6 }}>
          Sandbox keys are named <code>pk_test_…</code> / <code>sk_test_…</code>. Bookings made with them are
          <strong> fully simulated</strong> — no real hotel or tee time is reserved and <strong>no card is ever charged</strong> —
          so you can build and test your integration safely. Use the <a href={docsHref} target="_blank" rel="noopener" style={{ color: '#0066cc' }}>test card numbers</a> from the docs.
        </p>
        {activeTest.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}>
            {activeTest.map(k => (
              <KeyRow key={k.id} k={k} isAdmin={isAdmin} busy={busy} env={env} checkUrl={checkUrl}
                onRotate={() => rotate(k)} onRevoke={() => revoke(k)} onEditDomains={() => editDomains(k)}
                onEditIps={() => editIps(k)} onEditAlert={() => editAlert(k)} onAddIp={(ip) => addIp(k, ip)} />
            ))}
          </div>
        )}
        <button onClick={createSandbox} disabled={busy} style={activeTest.length ? secondaryBtn : primaryBtn}>
          {activeTest.length ? '+ New sandbox key pair' : 'Create sandbox keys (publishable + secret)'}
        </button>
      </div>

      {revoked.length > 0 && (
        <details style={{ marginTop: 16 }}>
          <summary style={{ cursor: 'pointer', color: '#64748b', fontSize: 13 }}>Revoked keys ({revoked.length})</summary>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
            {revoked.map(k => (
              <div key={k.id} style={{ ...keyCard, opacity: 0.6 }}>
                <code style={{ fontSize: 13 }}>{k.keyPrefix}</code>
                <span style={pill('#e2e8f0', '#475569')}>{k.keyType}</span>
                <span style={{ color: '#94a3b8', fontSize: 12 }}>revoked</span>
              </div>
            ))}
          </div>
        </details>
      )}

      <p style={{ fontSize: 12, color: '#64748b', marginTop: 16, lineHeight: 1.6 }}>
        <strong>Publishable keys</strong> (<code>pk_live_…</code>) are safe to use in a website's front-end code and only
        work from the allowed domains. <strong>Secret keys</strong> (<code>sk_live_…</code>) must stay on a server and can
        manage bookings — never put one in a web page. See the <a href={docsHref} target="_blank" rel="noopener" style={{ color: '#0066cc' }}>API documentation</a>.
      </p>
    </div>
  )
}

function EnvBanner({ env }) {
  const live = env === 'live'
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      background: live ? '#ecfdf5' : '#fffbeb',
      border: '1px solid ' + (live ? '#6ee7b7' : '#fcd34d'),
      color: live ? '#065f46' : '#92400e',
      borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 13, lineHeight: 1.5,
    }}>
      <span style={pill(live ? '#059669' : '#d97706', '#fff')}>{live ? 'LIVE' : 'DEV'}</span>
      <span>
        {live
          ? 'Production environment — these are live keys handling real bookings and payments.'
          : 'Development environment — these keys operate against test data and fake payments. Tokens are named pk_live_/sk_live_ in every environment, so this is a DEV key despite the name.'}
      </span>
    </div>
  )
}

function KeyRow({ k, isAdmin, busy, env, checkUrl, onRotate, onRevoke, onEditDomains, onEditIps, onEditAlert, onAddIp }) {
  const alertLabel = k.bookingAlertPerHour == null ? 'default' : (k.bookingAlertPerHour === 0 ? 'off' : `${k.bookingAlertPerHour}/hr`)
  const allowedIps = k.allowedIps || []
  return (
    <div style={keyCard}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <code style={{ fontSize: 14, fontWeight: 600 }}>{k.keyType === 'publishable' ? k.keyId : k.keyPrefix}</code>
          <span style={pill(k.keyType === 'secret' ? '#fee2e2' : '#dbeafe', k.keyType === 'secret' ? '#991b1b' : '#1e40af')}>{k.keyType}</span>
          {k.environment === 'test' && <span style={pill('#fef3c7', '#92400e')}>test mode</span>}
          {env && <span style={pill(env === 'live' ? '#d1fae5' : '#fef3c7', env === 'live' ? '#065f46' : '#92400e')}>{env === 'live' ? 'live' : 'dev'}</span>}
          <span style={{ fontSize: 11, color: '#64748b' }}>{k.scopes.join(' · ')}</span>
        </div>
        <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
          {k.keyType === 'publishable' && (
            <>Domains: {k.allowedDomains?.length ? k.allowedDomains.map(d => <code key={d} style={{ marginRight: 6 }}>{d}</code>) : <span style={{ color: '#dc2626' }}>none set (key won't work)</span>} · </>
          )}
          {k.requestCount?.toLocaleString?.() ?? k.requestCount} requests
          {k.lastUsedAt ? ` · last used ${new Date(k.lastUsedAt).toLocaleString()}` : ' · never used'}
        </div>
        {/* IP allowlist applies to any key type (it's enforced regardless of
            publishable/secret), so surface it for both. */}
        <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
          IPs: {allowedIps.length ? allowedIps.map(ip => <code key={ip} style={{ marginRight: 6 }}>{ip}</code>) : <span>any</span>}
          {' · '}Volume alert: {alertLabel}
        </div>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          {k.keyType === 'publishable' && <button onClick={onEditDomains} disabled={busy} style={linkBtn}>Edit domains</button>}
          <button onClick={onEditIps} disabled={busy} style={linkBtn}>Edit IP allowlist</button>
          <button onClick={onEditAlert} disabled={busy} style={linkBtn}>Volume alert</button>
        </div>

        <ConnectionCheck k={k} checkUrl={checkUrl} allowedIps={allowedIps} busy={busy} onAddIp={onAddIp} />
      </div>
      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
        <button onClick={onRotate} disabled={busy} style={secondaryBtnSm}>Rotate</button>
        {isAdmin && <button onClick={onRevoke} disabled={busy} style={dangerBtnSm}>Revoke</button>}
      </div>
    </div>
  )
}

// Self-service IP-allowlist helper: a copy-paste curl to run FROM the server
// that makes your API calls (so it reveals that server's egress IP, not a
// browser's), plus the distinct IPs we've recently seen this key call from —
// each one-click addable to the allowlist. Pairs with the /connection-check
// endpoint (which skips IP enforcement so it can't lock you out).
function ConnectionCheck({ k, checkUrl, allowedIps, busy, onAddIp }) {
  const tokenPlaceholder = k.keyType === 'publishable' ? (k.keyId || 'pk_live_…') : 'sk_live_YOUR_SECRET_KEY'
  const header = k.keyType === 'publishable' ? `X-Api-Key: ${tokenPlaceholder}` : `Authorization: Bearer ${tokenPlaceholder}`
  const curl = `curl -s -H "${header}" ${checkUrl}`
  const recent = k.recentIps || []
  return (
    <details style={{ marginTop: 8 }}>
      <summary style={{ cursor: 'pointer', color: '#0066cc', fontSize: 12 }}>Set up IP allowlist / find my IP</summary>
      <div style={{ marginTop: 8, fontSize: 12, color: '#475569', lineHeight: 1.6 }}>
        <p style={{ margin: '0 0 6px' }}>
          Locking a key to specific IPs adds a strong second factor for server-to-server calls.
          To find the IP we see for your calls, run this <strong>from the server that will make your API requests</strong>
          {k.keyType === 'secret' ? ' (with your real secret key)' : ''} — a browser would report the wrong IP:
        </p>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <code style={{ flex: 1, background: '#0f172a', color: '#e2e8f0', borderRadius: 6, padding: '8px 10px', fontSize: 12, wordBreak: 'break-all' }}>{curl}</code>
          <button type="button" onClick={() => navigator.clipboard?.writeText(curl)} style={secondaryBtnSm}>Copy</button>
        </div>
        <p style={{ margin: '8px 0 4px' }}>
          The response includes <code>yourIp</code> and a <code>recentIps</code> list. Add the value(s) to this key's
          allowlist via <em>Edit IP allowlist</em> above. If your servers scale or use several egress IPs, add a CIDR
          range that covers them.
        </p>
        {recent.length > 0 && (
          <div style={{ marginTop: 6 }}>
            <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Recent egress IPs we've seen (last 30 days)</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {recent.map(r => {
                const already = allowedIps.includes(r.ip)
                return (
                  <div key={r.ip} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <code style={{ fontSize: 12 }}>{r.ip}</code>
                    <span style={{ fontSize: 11, color: '#94a3b8' }}>{r.hits} call{r.hits === 1 ? '' : 's'} · last {new Date(r.lastSeen).toLocaleString()}</span>
                    {already
                      ? <span style={pill('#dcfce7', '#166534')}>allowed</span>
                      : <button type="button" onClick={() => onAddIp(r.ip)} disabled={busy} style={{ ...linkBtn, marginTop: 0 }}>Add to allowlist</button>}
                  </div>
                )
              })}
            </div>
          </div>
        )}
        {recent.length === 0 && (
          <p style={{ margin: '6px 0 0', color: '#94a3b8' }}>No calls recorded yet — once this key makes requests, the IPs we see will appear here.</p>
        )}
      </div>
    </details>
  )
}

function RevealBanner({ reveal, onClose }) {
  const items = []
  if (reveal.publishable) items.push(['Publishable key', reveal.publishable])
  if (reveal.secret) items.push(['Secret key', reveal.secret])
  if (reveal.single) items.push([`New ${reveal.keyType || ''} key`, reveal.single])
  return (
    <div style={{ background: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: 8, padding: 16, marginBottom: 16 }}>
      <div style={{ fontWeight: 700, color: '#065f46', marginBottom: 6 }}>Copy your key{items.length > 1 ? 's' : ''} now — the secret won't be shown again.</div>
      {items.map(([label, val]) => (
        <div key={label} style={{ marginTop: 8 }}>
          <div style={{ fontSize: 11, color: '#047857', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 2 }}>
            <code style={{ flex: 1, background: '#fff', border: '1px solid #a7f3d0', borderRadius: 6, padding: '8px 10px', fontSize: 13, wordBreak: 'break-all' }}>{val}</code>
            <button onClick={() => navigator.clipboard?.writeText(val)} style={secondaryBtnSm}>Copy</button>
          </div>
        </div>
      ))}
      <button onClick={onClose} style={{ ...secondaryBtn, marginTop: 12 }}>I've saved {items.length > 1 ? 'them' : 'it'}</button>
    </div>
  )
}

// Basic client-side domain validation (SEC #182). The server's normalizeDomain
// strips scheme/path/port, but we catch obvious mistakes before the round-trip
// and remind the reseller that www and the apex are separate hosts (the API
// does NOT strip www). Returns an array of human-readable problems (empty = ok).
function validateDomains(list) {
  const bad = []
  const hostRe = /^(?=.{1,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/
  for (const raw of list) {
    const s = String(raw).trim().toLowerCase()
    if (!s) continue
    if (/^https?:\/\//.test(s) || s.includes('/')) { bad.push(`"${raw}" — enter just the host, no http:// or /path`); continue }
    const host = s.replace(/:\d+$/, '') // tolerate an accidental :port, then validate the host
    if (!hostRe.test(host)) { bad.push(`"${raw}" — not a valid domain`); continue }
  }
  return bad
}

function pill(bg, fg) {
  return { display: 'inline-block', padding: '2px 8px', background: bg, color: fg, borderRadius: 999, fontSize: 11, fontWeight: 600 }
}
const keyCard = { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 14 }
const errorBox = { padding: 10, background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 6, fontSize: 13 }
const primaryBtn = { padding: '8px 16px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' }
const secondaryBtn = { padding: '8px 14px', background: '#fff', color: '#0f172a', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }
const secondaryBtnSm = { padding: '6px 12px', background: '#fff', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer' }
const dangerBtnSm = { padding: '6px 12px', background: '#fff', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer' }
const linkBtn = { background: 'none', border: 'none', color: '#0066cc', fontSize: 12, cursor: 'pointer', padding: '4px 0', marginTop: 2 }
